import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import * as React from 'react';

interface Step {
  id: string;
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (index: number) => void;
  allowNavigation?: boolean;
  /** Layout direction: vertical (default) or horizontal */
  orientation?: 'vertical' | 'horizontal';
  className?: string;
  /** Set of step indices that are currently loading (e.g., pre-fetching dynamic content) */
  loadingSteps?: Set<number>;
}

function Stepper({
  steps,
  currentStep,
  onStepClick,
  allowNavigation = true,
  orientation = 'vertical',
  className,
  loadingSteps,
}: StepperProps) {
  const safeCurrent = React.useMemo(() => {
    if (!steps.length) return 0;
    return Math.min(Math.max(currentStep, 0), steps.length - 1);
  }, [currentStep, steps.length]);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const stepRefs = React.useRef<(HTMLLIElement | null)[]>([]);

  const isVertical = orientation === 'vertical';

  // Scroll active step into view when currentStep changes
  React.useEffect(() => {
    if (stepRefs.current[safeCurrent] && scrollContainerRef.current) {
      const stepElement = stepRefs.current[safeCurrent];
      if (stepElement) {
        stepElement.scrollIntoView({
          behavior: 'smooth',
          block: isVertical ? 'nearest' : 'center',
          inline: isVertical ? 'center' : 'center',
        });
      }
    }
  }, [safeCurrent, isVertical]);

  const handleStepClick = (index: number) => {
    if (allowNavigation && onStepClick && index <= safeCurrent) {
      onStepClick(index);
    }
  };

  if (!steps.length) {
    return null;
  }

  const isScrollable = steps.length > 6;

  const stepCircleButton = (step: Step, index: number) => {
    const isCompleted = index < safeCurrent;
    const isCurrent = index === safeCurrent;
    const isLoading = loadingSteps?.has(index) ?? false;
    const isClickable = allowNavigation && (isCompleted || isCurrent);

    return (
      <button
        type="button"
        onClick={() => handleStepClick(index)}
        disabled={!isClickable}
        aria-current={isCurrent ? 'step' : undefined}
        aria-label={`Step ${index + 1}: ${step.title}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}${isLoading ? ' (loading)' : ''}`}
        className={cn(
          'group flex flex-col items-center gap-2.5 rounded-lg px-1 py-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue-500 focus-visible:ring-offset-2 relative z-10',
          isClickable && 'cursor-pointer',
          !isClickable && 'cursor-default'
        )}
      >
        <div
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-200 shrink-0',
            isCompleted && 'border-lime-green bg-[#fff] text-white shadow-sm',
            isCurrent &&
            'border-selise-blue-500 bg-brand-surface text-selise-blue-500 ring-2 ring-[hsl(var(--selise-blue))]/30 shadow-md',
            !isCompleted &&
            !isCurrent &&
            'border-border bg-muted text-muted-foreground',
            isLoading && !isCompleted && 'animate-pulse border-selise-blue/50',
            isClickable &&
            'group-hover:scale-110 group-hover:shadow-lg group-hover:border-selise-blue/80',
            isClickable && !isCompleted && !isCurrent && 'group-hover:bg-muted/80',
            isClickable && isCompleted && 'group-hover:bg-selise-blue/90',
            isClickable && isCurrent && 'group-hover:ring-[hsl(var(--selise-blue))]/50'
          )}
        >
          {isCompleted ? (
            <Check className="h-5 w-5 text-lime-green" strokeWidth={2.5} />
          ) : isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-lime-green" />
          ) : (
            <span className="font-medium">{index + 1}</span>
          )}
        </div>
      </button>
    );
  };

  const stepTitleButton = (step: Step, index: number) => {
    const isCompleted = index < safeCurrent;
    const isCurrent = index === safeCurrent;
    const isLoading = loadingSteps?.has(index) ?? false;
    const isClickable = allowNavigation && (isCompleted || isCurrent);

    return (
      <button
        type="button"
        onClick={() => handleStepClick(index)}
        disabled={!isClickable}
        aria-current={isCurrent ? 'step' : undefined}
        aria-label={`Step ${index + 1}: ${step.title}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}${isLoading ? ' (loading)' : ''}`}
        className={cn(
          'group flex w-full items-center rounded-lg px-2 py-1 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue focus-visible:ring-offset-2',
          isClickable && 'cursor-pointer',
          !isClickable && 'cursor-default'
        )}
      >
        <span
          className={cn(
            'text-sm font-medium leading-snug transition-colors duration-200',
            isCurrent && 'text-[hsl(var(--fg))] font-semibold',
            !isCompleted && !isCurrent && 'text-muted-foreground',
            isLoading && !isCompleted && 'text-lime-green'
          )}
        >
          {step.title}
        </span>
      </button>
    );
  };

  const stepsList =
    orientation === 'horizontal' ? (
      <ol
        className={cn(
          'relative flex flex-row items-center w-full',
          isScrollable ? 'py-4 px-4 overflow-x-auto' : 'py-4'
        )}
      >
        {steps.map((step, index) => {
          const segmentActive = index < safeCurrent;

          return (
            <li
              key={step.id}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              className={cn(
                'relative flex flex-row items-center justify-center flex-1 min-w-0 shrink-0 ',
                isScrollable && 'min-w-28'
              )}
            >
              <div className="flex flex-col items-center shrink-0">
                {stepCircleButton(step, index)}
                <div className="mt-2 flex justify-center max-w-28">
                  {stepTitleButton(step, index)}
                </div>
              </div>
              {index !== steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex-1 absolute translate-x-[calc(50%+28px)] top-7 left-5 right-5 mx-2 h-0.5 transition-colors duration-300 shrink-0 self-center',
                    segmentActive ? 'bg-lime-green' : 'bg-border'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    ) : (
      <ol
        className={cn(
          'relative flex flex-col items-stretch',
          isScrollable ? 'py-4 pr-4 pl-1' : 'py-4'
        )}
      >
        {steps.map((step, index) => {
          const topSegmentActive = index <= safeCurrent;
          const bottomSegmentActive = index < safeCurrent;

          return (
            <li
              key={step.id}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              className={cn(
                'relative flex items-stretch gap-3',
                index !== steps.length - 1 && 'pb-6'
              )}
            >
              <div className="relative flex flex-col items-center justify-center">
                {index !== 0 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute -top-6 h-6 w-0.5 transition-colors duration-300 z-0',
                      topSegmentActive ? 'bg-lime-green' : 'bg-border'
                    )}
                  />
                )}
                {index !== steps.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-6 h-6 w-0.5 transition-colors duration-300 z-0',
                      bottomSegmentActive ? 'bg-lime-green' : 'bg-border'
                    )}
                  />
                )}
                {stepCircleButton(step, index)}
              </div>
              <div className="flex-1 flex items-center pt-1">{stepTitleButton(step, index)}</div>
            </li>
          );
        })}
      </ol>
    );

  const scrollContainerClass = isVertical
    ? 'relative h-[calc(100vh-5rem)] overflow-y-auto px-2 pb-2 pt-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
    : 'relative overflow-x-auto px-2 pb-2 pt-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

  return (
    <nav aria-label="Progress" className={cn('w-full', className)}>
      {isScrollable ? (
        <div ref={scrollContainerRef} className={scrollContainerClass}>
          {stepsList}
        </div>
      ) : (
        <div className={cn(isVertical && 'mx-auto max-w-md', 'px-2')}>{stepsList}</div>
      )}
    </nav>
  );
}

// Compact variant for mobile or smaller spaces
function StepperCompact({
  steps,
  currentStep,
  className,
  loadingSteps,
}: Omit<StepperProps, 'onStepClick' | 'allowNavigation'>) {
  const safeCurrent = React.useMemo(() => {
    if (!steps.length) return 0;
    return Math.min(Math.max(currentStep, 0), steps.length - 1);
  }, [currentStep, steps.length]);

  return (
    <div
      className={cn('flex w-full items-center justify-center gap-1.5 px-4 py-3', className)}
      role="progressbar"
      aria-valuenow={safeCurrent + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label={`Step ${safeCurrent + 1} of ${steps.length}`}
    >
      {steps.map((_, index) => {
        const isCompleted = index < safeCurrent;
        const isCurrent = index === safeCurrent;
        const isLoading = loadingSteps?.has(index) ?? false;

        return (
          <div
            key={index}
            className={cn(
              'h-2 rounded-full transition-all duration-300',
              isCompleted && 'bg-selise-blue w-8',
              isCurrent && 'bg-selise-blue w-10 shadow-sm',
              !isCompleted && !isCurrent && 'bg-border w-6',
              // Loading state - pulsing animation for the loading step
              isLoading && !isCompleted && 'animate-pulse bg-selise-blue/60 w-8'
            )}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
}

export { Stepper, StepperCompact };
export type { Step, StepperProps };
